import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import manifest from "@/data/sampleManifest.json";

/**
 * SamplePreviewStrip — horizontally scrolling strip of page-1 tiles for the
 * featured sample lesson. Every tile links to /samples (no per-artifact deep
 * links). Tiles are 4:5, ~180px at a 390px viewport, with a partial tile
 * visible at the right edge so the scroll affordance is obvious. Static: reads
 * the committed manifest, no network, no generator.
 */
export default function SamplePreviewStrip() {
  const lesson = (manifest.lessons || [])[0];
  if (!lesson) return null;

  return (
    <section
      data-testid="home-samples-strip"
      aria-labelledby="home-samples-strip-heading"
      className="mx-auto mb-6 max-w-6xl px-6"
    >
      <h2
        id="home-samples-strip-heading"
        className="text-center text-xs font-bold uppercase tracking-[0.25em] text-[#4a6984]/70"
      >
        See a complete package
      </h2>
      <p
        data-testid="home-samples-strip-subhead"
        className="mx-auto mt-2 max-w-xl text-center text-sm text-[#4a6984]"
      >
        {lesson.title.split(":")[0]} — {lesson.grade} {lesson.subject}
      </p>

      <div
        data-testid="home-samples-strip-scroller"
        className="mt-5 flex snap-x gap-4 overflow-x-auto pb-3 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {lesson.artifacts.map((artifact) => (
          <Link
            key={artifact.slug}
            to="/samples"
            data-testid={`home-samples-tile-${artifact.slug}`}
            className="group relative w-[180px] flex-none snap-start overflow-hidden rounded-xl border border-[#4a6984]/15 bg-white shadow-sm transition-shadow hover:shadow-md"
          >
            <img
              src={`/samples/previews/${lesson.slug}/${artifact.slug}-tile.webp`}
              alt={artifact.name}
              loading="lazy"
              className="block aspect-[4/5] w-full object-cover object-top"
            />
            <span className="absolute inset-x-0 bottom-0 bg-[#1c2d3d]/85 px-2.5 py-1.5 text-[11px] font-bold leading-tight text-white">
              {artifact.name}
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-3 text-center">
        <Link
          to="/samples"
          data-testid="home-samples-strip-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4a6984] underline-offset-4 hover:text-[#1c2d3d] hover:underline"
        >
          See the complete package <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}
